/**
 * HTML sanitization utility for safe rendering with dangerouslySetInnerHTML.
 *
 * Allows safe formatting tags produced by FormRichTextEditor (Slate).
 * Strips dangerous elements, event handlers, and dangerous URI schemes.
 */

/** Tags allowed in rich text content (produced by FormRichTextEditor / Slate) */
const ALLOWED_RICH_TEXT_TAGS =
  /^(p|b|i|u|s|sub|sup|a|ol|li|br|indent|item|strong|em)$/i

/**
 * Sanitize rich text HTML for safe rendering.
 * Strips dangerous elements (script, iframe, style, etc.), event handlers,
 * and dangerous URI schemes. Preserves safe formatting tags.
 */
export const sanitizeHtml = (html: string): string => {
  let sanitized = html

  // 1. Strip dangerous elements entirely (content and all)
  sanitized = sanitized.replace(
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    "",
  )
  sanitized = sanitized.replace(
    /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
    "",
  )
  sanitized = sanitized.replace(
    /<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi,
    "",
  )
  sanitized = sanitized.replace(
    /<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi,
    "",
  )
  sanitized = sanitized.replace(/<embed\b[^>]*\/?>/gi, "")
  sanitized = sanitized.replace(
    /<form\b[^<]*(?:(?!<\/form>)<[^<]*)*<\/form>/gi,
    "",
  )
  sanitized = sanitized.replace(/<link\b[^>]*\/?>/gi, "")
  sanitized = sanitized.replace(/<meta\b[^>]*\/?>/gi, "")
  sanitized = sanitized.replace(/<base\b[^>]*\/?>/gi, "")

  // 2. Strip all event handler attributes (on*)
  sanitized = sanitized.replace(
    /\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi,
    "",
  )

  // 3. Strip dangerous URI schemes from href/src/action
  sanitized = sanitized.replace(
    /\b(href|src|action)\s*=\s*"(?:javascript|data|vbscript):[^"]*"/gi,
    "",
  )
  sanitized = sanitized.replace(
    /\b(href|src|action)\s*=\s*'(?:javascript|data|vbscript):[^']*'/gi,
    "",
  )

  // 4. Strip disallowed tags but keep their text content
  sanitized = sanitized.replace(
    /<\/?([a-z][a-z0-9]*)\b[^>]*\/?>/gi,
    (match, tagName: string) => {
      if (ALLOWED_RICH_TEXT_TAGS.test(tagName)) {
        return match
      }
      return ""
    },
  )

  return sanitized
}
